import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Sprint K · Attorney Declaration form.
 *
 * Route: /app/legal/analysis/attorney-declaration
 *
 * Owner (`platform_owner`) is redirected straight through — they never need
 * to declare. All other users must submit bar number + state + admission year
 * + practice_areas + explicit acknowledgment before the K-2 gate opens.
 */
export default function AttorneyDeclarationForm() {
    const nav = useNavigate();
    const { user } = useAuth();
    const isOwner = user?.role === "platform_owner";

    const [jurisdictions, setJurisdictions] = useState([]);
    const [practiceAreas, setPracticeAreas] = useState([]);
    const [existing, setExisting] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        bar_number: "",
        bar_state: "",
        bar_admission_year: new Date().getFullYear() - 5,
        practice_areas: [],
        acknowledged: false,
    });

    useEffect(() => {
        if (isOwner) {
            nav("/app/legal/analysis", { replace: true });
            return;
        }
        (async () => {
            try {
                const [j, p, m] = await Promise.all([
                    api.get("/legal/jurisdictions"),
                    api.get("/legal/practice-areas"),
                    api.get("/legal/attorney-declaration/me").catch((e) => {
                        if (e?.response?.status === 404) return { data: { declaration: null } };
                        throw e;
                    }),
                ]);
                setJurisdictions(j.data.jurisdictions || []);
                setPracticeAreas(p.data.practice_areas || []);
                setExisting(m.data.declaration || null);
                if (m.data.declaration) {
                    // If declaration already active, jump to /analysis.
                    nav("/app/legal/analysis", { replace: true });
                }
            } catch {
                toast.error("Could not load legal reference lists.");
            }
        })();
    }, [isOwner, nav]);

    const togglePracticeArea = (code) => {
        setForm((f) => ({
            ...f,
            practice_areas: f.practice_areas.includes(code)
                ? f.practice_areas.filter((c) => c !== code)
                : [...f.practice_areas, code],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.bar_number.trim()) return toast.error("Bar number is required.");
        if (!form.bar_state) return toast.error("Bar state is required.");
        if (!form.practice_areas.length) return toast.error("Select at least one practice area.");
        if (!form.acknowledged) return toast.error("You must acknowledge the declaration.");
        setBusy(true);
        try {
            await api.post("/legal/attorney-declaration", form);
            toast.success("Declaration recorded. Legal Analysis unlocked.");
            nav("/app/legal/analysis", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail?.message || err?.message || "Submission failed.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    if (isOwner || existing) return null;

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar" data-testid="attorney-declaration-page">
            <div className="max-w-2xl mx-auto p-8">
                <button
                    onClick={() => nav(-1)}
                    className="flex items-center gap-1.5 text-sm mb-6"
                    style={{ color: "var(--atlas-text-tertiary)" }}
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
                    Back
                </button>

                <div className="flex items-start gap-4 mb-8">
                    <div
                        className="w-12 h-12 rounded-lg grid place-items-center flex-shrink-0"
                        style={{
                            background: "rgba(139, 92, 246, 0.14)",
                            border: "1px solid rgba(139, 92, 246, 0.35)",
                        }}
                    >
                        <Scale className="w-6 h-6" style={{ color: "var(--atlas-primary)" }} strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Legal Analysis · Access gate
                        </div>
                        <h1
                            className="text-2xl font-semibold mt-1"
                            style={{ color: "var(--atlas-text-primary)" }}
                        >
                            Attorney Self-Declaration
                        </h1>
                        <p
                            className="mt-2 text-sm leading-relaxed"
                            style={{ color: "var(--atlas-text-secondary)" }}
                        >
                            Legal Analysis is gated behind attorney self-declaration. Nexus One does
                            not verify bar admission with external directories — this form is an
                            audit-only record. Falsifying it may expose you to bar sanction. Owner
                            accounts do not see this form.
                        </p>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="space-y-5 p-6 rounded-xl"
                    style={{
                        background: "var(--atlas-bg-elevated)",
                        border: "1px solid var(--atlas-border-strong)",
                    }}
                    data-testid="attorney-declaration-form"
                >
                    <div>
                        <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Bar Number
                        </label>
                        <input
                            type="text"
                            value={form.bar_number}
                            onChange={(e) => setForm({ ...form, bar_number: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg text-sm"
                            style={{
                                background: "var(--atlas-bg-deep)",
                                border: "1px solid var(--atlas-border-soft)",
                                color: "var(--atlas-text-primary)",
                            }}
                            placeholder="e.g. 285147"
                            data-testid="attorney-bar-number"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                Bar State
                            </label>
                            <select
                                value={form.bar_state}
                                onChange={(e) => setForm({ ...form, bar_state: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg text-sm"
                                style={{
                                    background: "var(--atlas-bg-deep)",
                                    border: "1px solid var(--atlas-border-soft)",
                                    color: "var(--atlas-text-primary)",
                                }}
                                data-testid="attorney-bar-state"
                                required
                            >
                                <option value="">Select state…</option>
                                {jurisdictions.map((j) => (
                                    <option key={j.code} value={j.code}>{j.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                Admission Year
                            </label>
                            <input
                                type="number"
                                min="1900"
                                max={new Date().getFullYear()}
                                value={form.bar_admission_year}
                                onChange={(e) => setForm({ ...form, bar_admission_year: parseInt(e.target.value || "0", 10) })}
                                className="w-full px-3 py-2 rounded-lg text-sm"
                                style={{
                                    background: "var(--atlas-bg-deep)",
                                    border: "1px solid var(--atlas-border-soft)",
                                    color: "var(--atlas-text-primary)",
                                }}
                                data-testid="attorney-admission-year"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Practice Areas (select at least one)
                        </label>
                        <div className="flex flex-wrap gap-2" data-testid="attorney-practice-areas">
                            {practiceAreas.map((p) => {
                                const on = form.practice_areas.includes(p.code);
                                return (
                                    <button
                                        type="button"
                                        key={p.code}
                                        onClick={() => togglePracticeArea(p.code)}
                                        className="px-3 py-1.5 rounded-full text-xs transition"
                                        style={{
                                            background: on ? "rgba(139,92,246,0.20)" : "var(--atlas-bg-deep)",
                                            border: `1px solid ${on ? "var(--atlas-primary)" : "var(--atlas-border-soft)"}`,
                                            color: on ? "var(--atlas-primary-hover)" : "var(--atlas-text-secondary)",
                                        }}
                                        data-testid={`attorney-pa-${p.code}`}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <label
                        className="flex items-start gap-3 cursor-pointer p-4 rounded-lg"
                        style={{
                            background: "var(--atlas-bg-deep)",
                            border: "1px solid var(--atlas-border-soft)",
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={form.acknowledged}
                            onChange={(e) => setForm({ ...form, acknowledged: e.target.checked })}
                            className="mt-0.5 w-4 h-4 flex-shrink-0"
                            data-testid="attorney-declaration-ack"
                        />
                        <span className="text-sm leading-relaxed" style={{ color: "var(--atlas-text-primary)" }}>
                            I hereby declare under penalty of perjury that I am admitted to the bar
                            in the state above, that my declaration is truthful, and I understand
                            Nexus One provides legal <strong>information</strong> (not legal advice)
                            generated by AI. All output must be reviewed by a licensed attorney
                            before any client reliance.
                        </span>
                    </label>

                    <div className="flex items-center gap-3">
                        <button
                            type="submit"
                            disabled={busy}
                            className="px-5 py-2.5 rounded-lg text-sm font-medium transition disabled:opacity-50"
                            style={{
                                background: "var(--atlas-primary)",
                                color: "white",
                                border: "1px solid var(--atlas-primary)",
                            }}
                            data-testid="attorney-declaration-submit"
                        >
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
                                {busy ? "Submitting…" : "Submit Declaration"}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => nav("/app/legal")}
                            className="px-4 py-2 rounded-lg text-sm"
                            style={{
                                border: "1px solid var(--atlas-border-strong)",
                                color: "var(--atlas-text-tertiary)",
                                background: "transparent",
                            }}
                            data-testid="attorney-declaration-back"
                        >
                            Back to Legal
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
